import {
  Badge,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui";
import { CheckCircle, ChevronDown, ChevronUp, Package } from "lucide-react";
import { useState } from "react";

interface OrderItem {
  title?: string;
  quantity?: number;
  variant_title?: string;
  variant_id?: number;
  price?: number;
}

interface ShippingAddress {
  first_name?: string;
  last_name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string;
}

export type {
  AddedLineItem,
  ApprovalDetails,
} from "@/lib/hooks/use-product-selection-types";
import type {
  AddedLineItem,
  ApprovalDetails,
} from "@/lib/hooks/use-product-selection-types";

export interface OrderApprovedConfirmationProps {
  orderRequestStatus: string;
  creatorOrderRequest: Record<string, unknown> | null;
  /** Brand-approved order payload; line_items may be a subset of the request. */
  details?: ApprovalDetails | null;
  /** Approved items the brand added that were not in the request. */
  addedLineItems?: AddedLineItem[];
  showPrice: boolean;
}

function parseItems(
  creatorOrderRequest: Record<string, unknown> | null,
): OrderItem[] {
  if (!creatorOrderRequest) return [];
  // API returns `line_items`
  const items = creatorOrderRequest.line_items;
  if (!Array.isArray(items)) return [];
  return items.map((item: Record<string, unknown>) => ({
    title: typeof item.title === "string" ? item.title : undefined,
    quantity: typeof item.quantity === "number" ? item.quantity : undefined,
    variant_title:
      typeof item.variant_title === "string" ? item.variant_title : undefined,
    variant_id:
      typeof item.variant_id === "number" ? item.variant_id : undefined,
    price: typeof item.price === "number" ? item.price : undefined,
  }));
}

/** Parse a raw PFA numeric field, rejecting null/empty instead of coercing to 0. */
function toFiniteNumber(
  value: number | string | null | undefined,
): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Map of approved variant_id → approved quantity from the order `details`. */
function parseApprovedVariants(
  details: ApprovalDetails | null | undefined,
): Map<number, number | null> {
  const approved = new Map<number, number | null>();
  for (const item of details?.line_items ?? []) {
    const variantId = toFiniteNumber(item?.variant_id);
    if (variantId == null) continue;
    approved.set(variantId, toFiniteNumber(item?.quantity));
  }
  return approved;
}

function parseShippingAddress(
  creatorOrderRequest: Record<string, unknown> | null,
): ShippingAddress | null {
  if (!creatorOrderRequest) return null;
  // API returns `shipping_address`
  const addr = creatorOrderRequest.shipping_address;
  if (!addr || typeof addr !== "object") return null;
  const a = addr as Record<string, unknown>;
  return {
    first_name: typeof a.first_name === "string" ? a.first_name : undefined,
    last_name: typeof a.last_name === "string" ? a.last_name : undefined,
    address1: typeof a.address1 === "string" ? a.address1 : undefined,
    address2: typeof a.address2 === "string" ? a.address2 : undefined,
    city: typeof a.city === "string" ? a.city : undefined,
    province: typeof a.province === "string" ? a.province : undefined,
    country: typeof a.country === "string" ? a.country : undefined,
    zip: typeof a.zip === "string" ? a.zip : undefined,
    phone: typeof a.phone === "string" ? a.phone : undefined,
  };
}

export function OrderApprovedConfirmation({
  orderRequestStatus,
  creatorOrderRequest,
  details = null,
  addedLineItems = [],
  showPrice,
}: OrderApprovedConfirmationProps) {
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const isAccepted = orderRequestStatus === "ACCEPTED";
  const items = parseItems(creatorOrderRequest);
  const shippingAddress = parseShippingAddress(creatorOrderRequest);
  const contactEmail =
    typeof creatorOrderRequest?.email === "string"
      ? creatorOrderRequest.email
      : null;

  const approvedVariants = isAccepted
    ? parseApprovedVariants(details)
    : new Map<number, number | null>();
  const approvedRequestedCount = items.filter(
    (item) => item.variant_id != null && approvedVariants.has(item.variant_id),
  ).length;
  // Approved same variants but with a changed (e.g. reduced) quantity.
  const hasQuantityChange = items.some(
    (item) =>
      item.variant_id != null &&
      approvedVariants.has(item.variant_id) &&
      (approvedVariants.get(item.variant_id) ?? null) !==
        (item.quantity ?? null),
  );
  // Partial approval: the brand approved a subset of the request, added items,
  // and/or changed quantities. Legacy orders with empty `details` keep the
  // original rendering.
  const isPartialApproval =
    isAccepted &&
    approvedVariants.size > 0 &&
    (approvedRequestedCount < items.length ||
      addedLineItems.length > 0 ||
      hasQuantityChange);
  // The count subline only reads sensibly when items were dropped or added —
  // for a quantity-only change the per-item qty carries the message.
  const showApprovedCountSubline =
    isPartialApproval &&
    items.length > 0 &&
    (approvedRequestedCount < items.length || addedLineItems.length > 0);

  const hasDetails =
    items.length > 0 ||
    shippingAddress ||
    contactEmail ||
    addedLineItems.length > 0;

  return (
    <div className="px-4 sm:px-6 py-6">
      <Card className="mt-4">
        <CardContent className="py-8 text-center">
          {isAccepted ? (
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
          ) : (
            <Package className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          )}
          <h2 className="text-lg font-semibold mb-2">
            {isAccepted ? "Order Approved" : "Request Submitted"}
          </h2>
          <p className="text-muted-foreground mb-1">
            {isAccepted
              ? "Your order has been approved by the brand!"
              : "Your product request has been submitted and is being reviewed by the brand."}
          </p>
          {showApprovedCountSubline && (
            <p className="text-muted-foreground mb-1">
              The brand approved {approvedRequestedCount} of your {items.length}{" "}
              requested products.
            </p>
          )}
          <Badge variant="secondary" className="mt-2">
            {orderRequestStatus}
          </Badge>

          {hasDetails && (
            <Collapsible
              open={isReviewOpen}
              onOpenChange={setIsReviewOpen}
              className="mt-6"
            >
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Review Order
                  {isReviewOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4 text-left">
                {(items.length > 0 || addedLineItems.length > 0) && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium mb-2">Items</h3>
                    <div className="space-y-2">
                      {items.map((item, index) => {
                        const isItemApproved =
                          isPartialApproval &&
                          item.variant_id != null &&
                          approvedVariants.has(item.variant_id);
                        const approvedQuantity =
                          isItemApproved && item.variant_id != null
                            ? approvedVariants.get(item.variant_id)
                            : null;
                        // For approved items show only the brand-approved
                        // quantity; never fall back to the requested one.
                        const displayQuantity = isItemApproved
                          ? approvedQuantity
                          : item.quantity;
                        return (
                          <div
                            key={`${item.title ?? "item"}-${String(index)}`}
                            className="border rounded-md p-3 text-sm"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium flex items-center gap-2">
                                {item.title ?? "Unnamed Item"}
                                {isItemApproved && (
                                  <Badge
                                    variant="secondary"
                                    className="bg-green-100 text-green-700 hover:bg-green-100"
                                  >
                                    Approved
                                  </Badge>
                                )}
                              </span>
                              {showPrice && item.price != null && (
                                <span className="text-muted-foreground">
                                  ${item.price.toFixed(2)}
                                </span>
                              )}
                            </div>
                            {displayQuantity != null && (
                              <div className="text-muted-foreground">
                                Qty: {displayQuantity}
                              </div>
                            )}
                            {item.variant_title && (
                              <div className="text-muted-foreground">
                                Variant: {item.variant_title}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {isPartialApproval &&
                        addedLineItems.map((item, index) => (
                          <div
                            key={`added-${String(item.variant_id)}-${String(index)}`}
                            className="border rounded-md p-3 text-sm"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium flex items-center gap-2">
                                {item.title ?? "Product"}
                                <Badge
                                  variant="secondary"
                                  className="bg-green-100 text-green-700 hover:bg-green-100"
                                >
                                  Added by brand
                                </Badge>
                              </span>
                              {showPrice && item.price != null && (
                                <span className="text-muted-foreground">
                                  ${item.price.toFixed(2)}
                                </span>
                              )}
                            </div>
                            {item.quantity != null && (
                              <div className="text-muted-foreground">
                                Qty: {item.quantity}
                              </div>
                            )}
                            {item.variant_title && (
                              <div className="text-muted-foreground">
                                Variant: {item.variant_title}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {shippingAddress && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium mb-2">
                      Shipping Address
                    </h3>
                    <div className="border rounded-md p-3 text-sm text-muted-foreground">
                      {shippingAddress.first_name || shippingAddress.last_name
                        ? `${shippingAddress.first_name ?? ""} ${shippingAddress.last_name ?? ""}`.trim()
                        : null}
                      {shippingAddress.address1 && (
                        <div>{shippingAddress.address1}</div>
                      )}
                      {shippingAddress.address2 && (
                        <div>{shippingAddress.address2}</div>
                      )}
                      {(shippingAddress.city ||
                        shippingAddress.province ||
                        shippingAddress.zip) && (
                        <div>
                          {[
                            shippingAddress.city,
                            shippingAddress.province,
                            shippingAddress.zip,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                      {shippingAddress.country && (
                        <div>{shippingAddress.country}</div>
                      )}
                      {shippingAddress.phone && (
                        <div>Phone: {shippingAddress.phone}</div>
                      )}
                    </div>
                  </div>
                )}

                {contactEmail && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Contact</h3>
                    <div className="border rounded-md p-3 text-sm text-muted-foreground">
                      {contactEmail}
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
